import Vue from 'vue';

const GlobalActions = {
    install(Vue, options = {}) {
        // Store router reference from options if provided
        let router = options.router;

        // Keep track of action execution timestamps to prevent rapid repeated triggering
        const actionTimestamps = {};
        const DEBOUNCE_TIME = 500; // ms

        // Create a global object to store actions
        const globalActions = {
            async updateProfile() {
                // Prevent rapid repeated triggering
                const now = Date.now();
                if (actionTimestamps.updateProfile && (now - actionTimestamps.updateProfile < DEBOUNCE_TIME)) {
                    console.log('Action updateProfile called too frequently, ignoring');
                    return;
                }
                actionTimestamps.updateProfile = now;

                // Access the store directly from Vue (set in app.js)
                const currentDevice = Vue.store?.getters?.currentDevice;
                console.log('currentDevice', currentDevice);

                // Check if currentDevice exists and model matches /mac/
                if (currentDevice?.model && /mac/i.test(currentDevice.model)) {
                    // Redirect to /appdb-tools
                    if (router) {
                        router.push('/appdb-tools');
                    } else if (Vue.prototype.$router) {
                        Vue.prototype.$router.push('/appdb-tools');
                    } else {
                        window.location.href = '/appdb-tools';
                    }
                    return;
                }

                const agree = await Vue.prototype.$confirm(Vue.prototype.$t('UPDATE_PROFILE_CONFIRM')).catch(() => false);
                if (agree) {
                    try {
                        const response = await Vue.prototype.$api.link({ type: 'update' }, false);
                        if (!response.success) {
                            Vue.prototype.$notify({
                                type: 'error',
                                text: response.errors?.[0]?.translated
                            });
                            return;
                        }

                        if (response.data?.profile_service) {
                            window.location.href = response.data.profile_service;
                        } else {
                            Vue.prototype.$notify({
                                type: 'error',
                                text: Vue.prototype.$t('SOMETHING_WENT_WRONG'),
                            });
                        }
                    } catch (error) {
                        Vue.prototype.$notify({
                            type: 'error',
                            text: Vue.prototype.$t('SOMETHING_WENT_WRONG'),
                        });
                    }
                }
            }
        };

        // Add to Vue prototype
        Vue.prototype.$globalActions = globalActions;

        // Function to determine if a link is internal
        const isInternalLink = (href) => {
            if (!href) return false;

            // Skip anchors, javascript links, or data URIs
            if (href.startsWith('#') ||
                href.startsWith('javascript:') ||
                href.startsWith('data:') ||
                href.startsWith('itms-services:') ||
                href.startsWith('shortcuts:')) {
                return false;
            }

            // Skip links with target="_blank"
            if (href.target === '_blank') {
                return false;
            }

            // Skip external links
            if (href.startsWith('http://') ||
                href.startsWith('https://') ||
                href.startsWith('//')) {
                return false;
            }

            return true;
        };

        // Add global click event listener to handle data-action attributes and internal links
        document.addEventListener('click', (event) => {
            // Handle data-action attributes
            let target = event.target;
            while (target && target !== document) {
                const action = target.getAttribute('data-action');
                if (action && globalActions[action]) {
                    event.preventDefault();

                    // Check if element has a processing flag to prevent multiple clicks
                    if (target.dataset.processing === 'true') {
                        console.log(`Action ${action} is already being processed`);
                        return;
                    }

                    // Set processing flag
                    target.dataset.processing = 'true';

                    // Execute action and clear flag when done
                    Promise.resolve(globalActions[action]())
                        .finally(() => {
                            setTimeout(() => {
                                target.dataset.processing = 'false';
                            }, DEBOUNCE_TIME);
                        });
                    return;
                }

                // Check if it's a link element
                if (target.tagName === 'A') {
                    const href = target.getAttribute('href');

                    // Handle internal links with Vue Router
                    if (isInternalLink(href)) {
                        try {
                            event.preventDefault();

                            // Try to get router if not available yet
                            if (!router) {
                                router = Vue.prototype.$router;
                            }

                            // If router is still not available, fall back to regular navigation
                            if (!router) {
                                console.warn('Vue Router not available, using regular navigation for:', href);
                                window.location.href = href;
                                return;
                            }

                            // Skip navigation if we're already on this route
                            if (router.currentRoute.path === href) {
                                console.log('Skipping navigation to current route:', href);
                                return;
                            }

                            // Use Vue Router to navigate
                            router.push(href).catch(err => {
                                // Only log navigation errors other than redundant navigation
                                if (err.name !== 'NavigationDuplicated') {
                                    console.error('Navigation error:', err);
                                }
                            });
                            return;
                        } catch (err) {
                            console.error('Error handling internal link:', err);
                            // Fallback to regular navigation on error
                            window.location.href = href;
                        }
                    }
                }

                target = target.parentNode;
            }
        });
    }
};

export default GlobalActions; 